/**
 * DOM SVG glyph component (HEX-102, ADR-0020 §3) — the UI-side renderer of the
 * shared glyph set. Builds an `<svg>` from the one path source ([[glyphPaths]]
 * `GLYPHS`) using `createElementNS`, so the same geometry the Pixi canvas draws
 * also reads in DOM chrome (shop, inventory, tooltips, legend).
 *
 * Colour is `currentColor` by default, so a glyph inherits the CSS `color` of
 * its host — set that to a Moss & Bone theme var (`var(--bone)`, `var(--ember)`,
 * `var(--t-attack)`, …) and the glyph follows. Pass `colour` to set it inline.
 *
 * Geometry only (the module's data-only contract): the caller owns layout.
 */

import { GLYPHS, GLYPH_VIEWBOX, type GlyphName, type GlyphOp } from './glyphPaths';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface DomGlyphOptions {
  /** Pixel width/height of the square svg. Default `18` (handoff default). */
  readonly size?: number;
  /**
   * Inline `color` applied to the svg, which the shapes inherit via
   * `currentColor`. Omit to inherit the host's CSS `color` (the theme-var path).
   */
  readonly colour?: string;
  /** Stroke width in viewBox units. Default the set's `DEFAULT_GLYPH_STROKE_WIDTH`. */
  readonly strokeWidth?: number;
  /** Optional class on the root svg. */
  readonly className?: string;
}

function appendOp(svg: SVGSVGElement, op: GlyphOp, strokeWidth: number | undefined): void {
  let el: SVGElement;
  switch (op.t) {
    case 'path': {
      el = document.createElementNS(SVG_NS, 'path');
      el.setAttribute('d', op.d);
      break;
    }
    case 'circle': {
      el = document.createElementNS(SVG_NS, 'circle');
      el.setAttribute('cx', String(op.cx));
      el.setAttribute('cy', String(op.cy));
      el.setAttribute('r', String(op.r));
      break;
    }
    case 'rect': {
      el = document.createElementNS(SVG_NS, 'rect');
      el.setAttribute('x', String(op.x));
      el.setAttribute('y', String(op.y));
      el.setAttribute('width', String(op.w));
      el.setAttribute('height', String(op.h));
      el.setAttribute('rx', String(op.rx));
      break;
    }
  }
  if (op.mode === 'stroke') {
    el.setAttribute('fill', 'none');
    el.setAttribute('stroke', 'currentColor');
    if (strokeWidth !== undefined) el.setAttribute('stroke-width', String(strokeWidth));
    el.setAttribute('stroke-linecap', 'round');
    el.setAttribute('stroke-linejoin', 'round');
  } else {
    el.setAttribute('fill', 'currentColor');
    el.setAttribute('stroke', 'none');
  }
  svg.appendChild(el);
}

/**
 * Build an `<svg>` element for `name`. Decorative by default (`aria-hidden`) —
 * glyphs paired with a text label carry no standalone meaning; a caller using a
 * glyph as the sole content of a control should add its own accessible label.
 */
export function createGlyphElement(name: GlyphName, options: DomGlyphOptions = {}): SVGSVGElement {
  const size = options.size ?? 18;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${GLYPH_VIEWBOX} ${GLYPH_VIEWBOX}`);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  if (options.className) svg.setAttribute('class', options.className);
  if (options.colour) svg.style.color = options.colour;
  for (const op of GLYPHS[name]) appendOp(svg, op, options.strokeWidth);
  return svg;
}
